import React, { useEffect } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { navigationIntegration, setSentryUser, clearSentryUser } from '../config/sentry';
import { analyticsService } from '../services/analytics';
import { RootStackParamList, User } from '../types';
import { useUser } from '../contexts';
import {
  LoginScreen,
  RegisterScreen,
  HomeScreen,
  ItemDetailsScreen,
  ItemFormScreen,
  BrowseScreen,
  ProfileScreen,
  ClubsScreen,
  ClubDetailScreen,
  ClubLeaderboardScreen,
  AdminDashboardScreen,
  AdminSettingsScreen,
  ManageUsersScreen,
  ManageClubsScreen,
  AdminModerationScreen,
  AdminListingsScreen,
  AdminAnalyticsScreen,
  OnboardingScreen,
  ConversationsListScreen,
  ChatScreen,
  AdminOptionListsScreen,
  AdminCustomValuesScreen,
  AdminPayoutsScreen,
  AdminTransactionsScreen,
  ForgotPasswordScreen,
  ResetPasswordScreen,
  ChangePasswordScreen,
  DeleteAccountScreen,
  EditProfileScreen,
  PrivacyPolicyScreen,
  MarkAsSoldScreen,
} from '../screens';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { linkingConfig } from '../config/linking';
import { Colors } from '../constants';
import { useSessionGuard } from '../hooks/useSessionGuard';
import { useActivityTracking } from '../hooks/useActivityTracking';
import { ReAuthDialog } from '../components';
import { navigationRef } from '../utils/navigationRef';

const Stack = createNativeStackNavigator<RootStackParamList>();

/** Maps route names to their i18n title keys for document.title on web */
const ROUTE_TITLE_KEYS: Record<string, string> = {
  Login: 'screens.login.title',
  Register: 'screens.register.title',
  ForgotPassword: 'screens.forgotPassword.title',
  ResetPassword: 'screens.resetPassword.title',
  ChangePassword: 'screens.changePassword.title',
  EditProfile: 'screens.profile.editProfileTitle',
  PrivacyPolicy: 'screens.privacy.title',
  onboarding: 'screens.onboarding.title',
  Home: 'screens.home.title',
  ItemDetails: 'screens.itemDetails.title',
  ItemForm: 'screens.itemForm.title',
  Browse: 'screens.browse.title',
  Profile: 'screens.profile.title',
  Clubs: 'screens.clubs.title',
  ClubDetail: 'screens.clubDetail.title',
  ClubLeaderboard: 'screens.clubs.title',
  AdminDashboard: 'screens.adminDashboard.title',
  AdminSettings: 'screens.adminSettings.title',
  ManageUsers: 'screens.manageUsers.title',
  ManageClubs: 'screens.manageClubs.title',
  AdminModeration: 'screens.adminModeration.title',
  AdminListings: 'screens.adminListings.title',
  AdminOptionLists: 'screens.adminOptionLists.title',
  AdminCustomValues: 'screens.adminCustomValues.title',
  AdminPayouts: 'screens.adminPayouts.title',
  AdminTransactions: 'screens.adminTransactions.title',
  ConversationsList: 'screens.conversations.title',
  Chat: 'screens.chat.title',
  MarkAsSold: 'screens.markAsSold.title',
};

// Wrapper component that has access to navigation context
const NavigationContent: React.FC<{ user: User | null; isPasswordRecovery: boolean }> = ({ user, isPasswordRecovery }) => {
  // Password recovery takes priority: show reset screen regardless of auth state
  if (isPasswordRecovery) {
    return (
      <View style={{ flex: 1 }}>
        <Stack.Navigator screenOptions={{ headerShown: false }}>
          <Stack.Screen name="ResetPassword" component={ResetPasswordScreen} />
        </Stack.Navigator>
      </View>
    );
  }

  // Check if user needs onboarding
  const needsOnboarding = user && !user.onboarding_completed;

  return (
    <View style={{ flex: 1 }}>
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
        }}
      >
        {needsOnboarding ? (
          // Onboarding screen for authenticated users who haven't completed onboarding
          <Stack.Screen
            name="onboarding"
            component={OnboardingScreen}
            options={{
              animation: 'fade',
            }}
          />
        ) : user ? (
          // Authenticated screens (only shown after onboarding is complete)
          <>
            <Stack.Screen
              name="Home"
              component={HomeScreen}
              options={{
                animation: Platform.OS === 'web' ? 'none' : 'fade',
              }}
            />
            <Stack.Screen
              name="ItemDetails"
              component={ItemDetailsScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="ItemForm"
              component={ItemFormScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="Browse"
              component={BrowseScreen}
              options={{
                animation: Platform.OS === 'web' ? 'none' : 'fade',
              }}
            />
            <Stack.Screen
              name="Profile"
              component={ProfileScreen}
              options={{
                animation: Platform.OS === 'web' ? 'none' : 'fade',
              }}
            />
            <Stack.Screen
              name="Clubs"
              component={ClubsScreen}
              options={{
                animation: Platform.OS === 'web' ? 'none' : 'fade',
              }}
            />
            <Stack.Screen
              name="ClubDetail"
              component={ClubDetailScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="ClubLeaderboard"
              component={ClubLeaderboardScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="AdminDashboard"
              component={AdminDashboardScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="AdminSettings"
              component={AdminSettingsScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="ManageUsers"
              component={ManageUsersScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="ManageClubs"
              component={ManageClubsScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="AdminModeration"
              component={AdminModerationScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="AdminListings"
              component={AdminListingsScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="AdminAnalytics"
              component={AdminAnalyticsScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="ConversationsList"
              component={ConversationsListScreen}
              options={{
                animation: Platform.OS === 'web' ? 'none' : 'fade',
              }}
            />
            <Stack.Screen
              name="Chat"
              component={ChatScreen}
              options={{
                animation: Platform.OS === 'web' ? 'none' : 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="AdminOptionLists"
              component={AdminOptionListsScreen}
              options={{ animation: 'slide_from_right' }}
            />
            <Stack.Screen
              name="AdminCustomValues"
              component={AdminCustomValuesScreen}
              options={{ animation: 'slide_from_right' }}
            />
            <Stack.Screen
              name="AdminPayouts"
              component={AdminPayoutsScreen}
              options={{ animation: 'slide_from_right' }}
            />
            <Stack.Screen
              name="AdminTransactions"
              component={AdminTransactionsScreen}
              options={{ animation: 'slide_from_right' }}
            />
            <Stack.Screen
              name="EditProfile"
              component={EditProfileScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="ChangePassword"
              component={ChangePasswordScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="PrivacyPolicy"
              component={PrivacyPolicyScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="DeleteAccount"
              component={DeleteAccountScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen
              name="MarkAsSold"
              component={MarkAsSoldScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />

          </>
        ) : (
          // Guest screens — marketplace is publicly accessible
          <>
            <Stack.Screen
              name="Browse"
              component={BrowseScreen}
              options={{
                animation: Platform.OS === 'web' ? 'none' : 'fade',
              }}
            />
            <Stack.Screen
              name="ItemDetails"
              component={ItemDetailsScreen}
              options={{
                animation: 'slide_from_right',
              }}
            />
            <Stack.Screen name="Login" component={LoginScreen} />
            <Stack.Screen name="Register" component={RegisterScreen} />
            <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
            <Stack.Screen name="ResetPassword" component={ResetPasswordScreen} />
          </>
        )}
      </Stack.Navigator>
    </View>
  );
};

export const AppNavigator: React.FC = () => {
  const { t } = useTranslation();
  const { user, loading, isPasswordRecovery } = useUser();

  // Session guard for re-authentication
  const sessionGuard = useSessionGuard(user !== null);

  // Track user activity for offline detection (email notifications)
  useActivityTracking(user?.id);
  // Set Sentry user context when user changes
  useEffect(() => {
    if (user) {
      setSentryUser(user.id);
    } else {
      clearSentryUser();
    }
  }, [user]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingText}>{t('navigation.loading')}</Text>
      </View>
    );
  }

  return (
    <>
      <NavigationContainer
        ref={navigationRef}
        linking={linkingConfig}
        documentTitle={{
          formatter: (_options, route) => {
            const key = route?.name ? ROUTE_TITLE_KEYS[route.name] : undefined;
            const screenTitle = key ? t(key) : route?.name;
            return screenTitle ? `Clubswap – ${screenTitle}` : 'Clubswap';
          },
        }}
        onReady={() => {
          // Register navigation container with Sentry (mobile only)
          navigationIntegration?.registerNavigationContainer(navigationRef);
          // Track initial screen on app load
          const currentRoute = navigationRef.getCurrentRoute();
          if (currentRoute?.name) {
            analyticsService.trackScreenView(currentRoute.name);
          }
        }}
        onStateChange={(state) => {
          if (state) {
            const currentRoute = state.routes[state.index];
            if (currentRoute?.name) {
              analyticsService.trackScreenView(currentRoute.name);
            }
          }
        }}
      >
        <NavigationContent user={user} isPasswordRecovery={isPasswordRecovery} />
      </NavigationContainer>

      {/* Re-auth dialog overlay */}
      <ReAuthDialog
        visible={sessionGuard.showDialog}
        onSuccess={sessionGuard.onSuccess}
        userEmail={sessionGuard.userEmail}
      />
    </>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.background,
  },
  loadingText: {
    fontSize: 18,
    color: Colors.textMedium,
  },
});
